import { Building2, Download, Search, Upload, User, UserPlus, Users, X } from "lucide-react"
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material"
import { alpha } from "@mui/material/styles"
import type { ContactList, ContactListMember } from "@crm-src/lib/contact-lists-api"
import { canManageGroup } from "@crm-src/pages/contact-groups/membership"

interface MemberPagination {
  current_page: number
  total_pages: number
  total_count: number
  per_page: number
}

interface ViewMembersDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  selectedGroup: ContactList | null
  members: ContactListMember[]
  membersLoading: boolean
  memberSearch: string
  onMemberSearchChange: (value: string) => void
  memberSearchTerm: string
  memberPagination: MemberPagination
  onMemberPageChange: (page: number) => void
  onExport: (group: ContactList) => void
  onOpenImport: (group: ContactList) => void
  onOpenAddMembers: (group: ContactList) => void
  onRemoveMember: (membershipId: number) => void
}

// Per-member-type badge tints (no theme palette token for these categories).
const memberBadgeStyle = (memberType: string) =>
  memberType === 'Customer'
    ? { bgcolor: "#dbeafe", color: "#1e40af" }
    : memberType === 'Lead'
      ? { bgcolor: "#dcfce7", color: "#166534" }
      : { bgcolor: "#f3e8ff", color: "#6b21a8" }

export function ViewMembersDialog({
  open,
  onOpenChange,
  selectedGroup,
  members,
  membersLoading,
  memberSearch,
  onMemberSearchChange,
  memberSearchTerm,
  memberPagination,
  onMemberPageChange,
  onExport,
  onOpenImport,
  onOpenAddMembers,
  onRemoveMember,
}: ViewMembersDialogProps) {
  // Anyone who can see the group can read and export it; changing its members
  // is the owner's or an admin's (the server's `can_manage` verdict).
  const canManage = canManageGroup(selectedGroup)
  return (
    <Dialog open={open} onClose={() => onOpenChange(false)} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box component="span" sx={{ color: "#d97706", display: "inline-flex" }}>
          <Users size={20} />
        </Box>
        {selectedGroup?.name}
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 1 }}>
          {selectedGroup?.description || 'No description'}
        </DialogContentText>

        <Box sx={{ py: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {memberPagination.total_count} member{memberPagination.total_count !== 1 ? 's' : ''}
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                startIcon={<Download size={16} />}
                onClick={() => onExport(selectedGroup!)}
                disabled={memberPagination.total_count === 0}
              >
                Export
              </Button>
              {canManage && (
              <>
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                startIcon={<Upload size={16} />}
                onClick={() => {
                  onOpenChange(false)
                  onOpenImport(selectedGroup!)
                }}
              >
                Import
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={<UserPlus size={16} />}
                onClick={() => {
                  onOpenChange(false)
                  onOpenAddMembers(selectedGroup!)
                }}
              >
                Add Members
              </Button>
              </>
              )}
            </Box>
          </Box>

          {/* Member Search */}
          <TextField
            fullWidth
            size="small"
            placeholder="Search members..."
            value={memberSearch}
            onChange={(e) => onMemberSearchChange(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Box component="span" sx={{ color: "text.disabled", display: "inline-flex" }}>
                    <Search size={16} />
                  </Box>
                </InputAdornment>
              ),
            }}
          />

          {membersLoading ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} variant="rounded" sx={{ height: 48 }} />
              ))}
            </Box>
          ) : members.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
              <Box sx={{ display: "flex", justifyContent: "center", mb: 1.5, color: "text.disabled" }}>
                <Users size={48} />
              </Box>
              {memberSearchTerm ? (
                <>
                  <Typography variant="body2">No members matching &quot;{memberSearchTerm}&quot;</Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>Try a different search term</Typography>
                </>
              ) : (
                <>
                  <Typography variant="body2">No members in this group yet</Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>Add contacts, customers, or prospects to this group</Typography>
                </>
              )}
            </Box>
          ) : (
            <>
              <Box sx={{ height: 320, overflow: "auto" }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {members.map((member) => (
                    <Box
                      key={member.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        p: 1.5,
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 1,
                        "&:hover": { bgcolor: "surfaceSoft" },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Box sx={{ flexShrink: 0, width: 32, height: 32, bgcolor: "surfaceSoft", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          {member.member_type === 'Customer' ? (
                            <Box component="span" sx={{ color: "#2563eb", display: "inline-flex" }}><Building2 size={16} /></Box>
                          ) : member.member_type === 'Lead' ? (
                            <Box component="span" sx={{ color: "#16a34a", display: "inline-flex" }}><UserPlus size={16} /></Box>
                          ) : (
                            <Box component="span" sx={{ color: "#9333ea", display: "inline-flex" }}><User size={16} /></Box>
                          )}
                        </Box>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{member.name || 'Unknown'}</Typography>
                          <Typography variant="caption" sx={{ color: "text.secondary" }}>{member.email || 'No email'}</Typography>
                        </Box>
                        <Box
                          component="span"
                          sx={{ fontSize: "0.75rem", px: 1, py: 0.25, borderRadius: 1, fontWeight: 500, ...memberBadgeStyle(member.member_type) }}
                        >
                          {member.member_type === 'Lead' ? 'Prospect' : member.member_type}
                        </Box>
                      </Box>
                      {canManage && (
                      <IconButton
                        size="small"
                        aria-label="Remove member"
                        onClick={() => onRemoveMember(member.id)}
                        sx={{ color: "error.main", "&:hover": { color: "error.dark", bgcolor: (theme) => alpha(theme.palette.error.main, 0.08) } }}
                      >
                        <X size={16} />
                      </IconButton>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Member Pagination */}
              {memberPagination.total_pages > 1 && (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pt: 1.5, borderTop: 1, borderColor: "divider", mt: 1.5 }}>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    Page {memberPagination.current_page} of {memberPagination.total_pages}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Button
                      variant="outlined"
                      color="inherit"
                      size="small"
                      onClick={() => onMemberPageChange(memberPagination.current_page - 1)}
                      disabled={memberPagination.current_page === 1}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outlined"
                      color="inherit"
                      size="small"
                      onClick={() => onMemberPageChange(memberPagination.current_page + 1)}
                      disabled={memberPagination.current_page === memberPagination.total_pages}
                    >
                      Next
                    </Button>
                  </Box>
                </Box>
              )}
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={() => onOpenChange(false)}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}
